/**
 * The phone's scans for every staff page: at start and when the network comes back, the events this person may
 * scan offline are remembered and the waiting scans are sent; what had waited is summed up until dismissed.
 * Nothing in a browser, nothing in an older app without scanning. docs/superpowers/specs/2026-10-31-mobile-app-part3-scanning-design.md
 */
import { useEffect, useState } from 'react'
import { useAuthStore } from '../../store/auth'
import { rentalApi } from '../rentalApi'
import { appBridge } from './bridge'
import { forgetEvents, onScanAnswer, rememberEvents, sendScans, type ScanAnswer, type ScanEntry } from './scans'
import { useT } from '../../i18n'

type Summary = { loaded: number; returned: number; unknown: number; refused: { code: string; reason: string }[] }
const EMPTY: Summary = { loaded: 0, returned: 0, unknown: 0, refused: [] }

function counted(s: Summary, e: ScanEntry, a: ScanAnswer): Summary {
  if (a.kind === 'done') return e.mode === 'load' ? { ...s, loaded: s.loaded + 1 } : { ...s, returned: s.returned + 1 }
  if (a.kind === 'unknown') return { ...s, unknown: s.unknown + 1 }
  if (a.kind === 'refused') return { ...s, refused: [...s.refused, { code: e.code, reason: a.reason }] }
  return s
}

export default function ScanBridge() {
  const t = useT()
  const { identityId, activeCompanyId, company, hasModule, hasPermission } = useAuthStore()
  const [summary, setSummary] = useState<Summary | null>(null)
  // the planners and the stock people, in a company with Rental
  const may = !!identityId && !!activeCompanyId && hasModule('rental') && (hasPermission('RENTAL_PLAN') || hasPermission('STOCK_MANAGE'))
  const companyName = company?.name ?? ''
  useEffect(() => {
    const app = appBridge()
    if (!app?.features.scan) return
    // signed in as someone who may not load (or in a company without Rental): nothing to offer offline
    if (identityId && !may) { void forgetEvents(); return }
    if (!may || !activeCompanyId) return
    const round = () => {
      void rentalApi.scanEvents().then(events => rememberEvents(activeCompanyId, companyName, events)).catch(() => undefined)
      void sendScans()
    }
    // only scans that had to wait are summed up: the scan view shows the others as they are answered
    const off = onScanAnswer((e, a) => { if (e.waited && a.kind !== 'wait') setSummary(s => counted(s ?? EMPTY, e, a)) })
    round()
    const offNetwork = app.onNetwork(online => { if (online) round() })
    return () => { off(); offNetwork() }
  }, [identityId, activeCompanyId, may, companyName])
  if (!summary) return null
  return (
    <div data-scan-summary role="status" style={{
      position: 'fixed', bottom: 'calc(env(safe-area-inset-bottom, 0px) + 8px)', left: 8, right: 8, zIndex: 1000, maxHeight: '50vh', overflowY: 'auto',
      padding: '10px 12px', borderRadius: 12, background: 'var(--bg-2)', border: '1px solid var(--line)', boxShadow: '0 6px 24px rgb(0 0 0 / .25)', fontSize: 13,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <strong style={{ flex: 1 }}>{t('rental.scan.summaryTitle')}</strong>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSummary(null)}>{t('rental.scan.dismiss')}</button>
      </div>
      <div>{t('rental.scan.summary', { loaded: summary.loaded, returned: summary.returned, refused: summary.refused.length })}</div>
      {summary.unknown > 0 && <div style={{ color: 'var(--fg-3)' }}>{t('rental.scan.unknownCount', { count: summary.unknown })}</div>}
      {summary.refused.length > 0 && (
        <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
          {summary.refused.map((r, i) => <li key={i} style={{ overflowWrap: 'anywhere' }}><code>{r.code}</code>: {r.reason}</li>)}
        </ul>
      )}
    </div>
  )
}
